import { useEffect, useRef, useState } from "react";
import { useAdminAuth } from "@/hooks/useAdminAuth";

interface Props {
  onVerified: () => void;
  onBack: () => void;
}

export default function MFAVerifyPage({ onVerified, onBack }: Props) {
  const { verifyLoginMFA, error, setError } = useAdminAuth();
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const submitToken = async (token: string) => {
    setLoading(true);
    const ok = await verifyLoginMFA(token);
    setLoading(false);

    if (ok) {
      onVerified();
      return;
    }

    setDigits(["", "", "", "", "", ""]);
    setTimeout(() => inputRefs.current[0]?.focus(), 50);
  };

  const handleChange = (index: number, value: string) => {
    setError("");
    const cleaned = value.replace(/\D/g, "").slice(-1);
    const nextDigits = [...digits];
    nextDigits[index] = cleaned;
    setDigits(nextDigits);

    if (cleaned && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (cleaned && index === 5) {
      const token = nextDigits.join("");
      if (token.length === 6) {
        void submitToken(token);
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length !== 6) return;
    setDigits(pasted.split(""));
    void submitToken(pasted);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = digits.join("");
    if (token.length !== 6) return;
    await submitToken(token);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-stone-100 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-orange-500 rounded-2xl mb-4">
            <i className="ri-smartphone-line text-white text-2xl"></i>
          </div>
          <h1 className="text-2xl font-bold text-slate-800">
            Verificação em Duas Etapas
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Digite o código atual de 6 dígitos do Google Authenticator
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-4 text-center">
                Código do Google Authenticator
              </label>
              <div className="flex gap-2 justify-center" onPaste={handlePaste}>
                {digits.map((digit, index) => (
                  <input
                    key={index}
                    ref={(element) => {
                      inputRefs.current[index] = element;
                    }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className={`w-12 h-14 text-center text-xl font-bold font-mono border-2 rounded-xl transition focus:outline-none ${
                      digit
                        ? "border-orange-400 bg-orange-50 text-orange-700"
                        : "border-slate-200 text-slate-800 focus:border-orange-400"
                    }`}
                  />
                ))}
              </div>
              <p className="text-center text-xs text-slate-400 mt-3">
                O código muda automaticamente a cada 30 segundos
              </p>
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                <i className="ri-error-warning-line text-red-500 text-sm"></i>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || digits.join("").length !== 6}
              className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-slate-200 disabled:text-slate-400 text-white font-semibold py-3 rounded-xl transition text-sm cursor-pointer whitespace-nowrap"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i> Validando código...
                </span>
              ) : (
                "Verificar código"
              )}
            </button>

            <button
              type="button"
              onClick={onBack}
              className="w-full text-slate-500 hover:text-slate-700 text-sm py-2 transition cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-1"></i> Voltar ao login
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
